import {
    FetchState,
    HsaBankTaxDocumentsState,
    HsaBankTaxDocumentsForYear,
    HsaBankTaxDocumentList,
    FeatureFlags,
    GeneratedPdfState,
    AnonDocumentState,
    AnonDocumentKey,
    UserFeedbackEventAction
} from "@/shared/types";
import Vue from "vue";
import * as BankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import moment from "moment";
import bankResource from "@/shared/resource/bank";
import { defineStore } from "pinia";
import _ from "lodash";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserFeedbackStore } from "@/employee/store/userFeedback";
import { errorIsStatus } from "@/shared/errorHelper";
import { openUrl } from "@/shared/urlHelper";

const currentYear = moment().year();

export const initialState: HsaBankTaxDocumentsState = {
    year: 0,
    documentsPerYear: {},
    pdfs: {},
    anonDocuments: {},
};

function emptyGeneratedPdfState (): GeneratedPdfState {
    return {
        fetchState: FetchState.Uninitialized,
        pdfData: null
    };
}

function emptyAnonDocumentState (): AnonDocumentState {
    return {
        fetchState: FetchState.Uninitialized,
        documentKey: null
    };
}

export const useHsaBankTaxDocumentsStore = defineStore("hsaBankTaxDocuments", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        isLoadedForYear: state => (year: number) => year > currentYear ||
            (state.documentsPerYear[year] && state.documentsPerYear[year].fetchState === FetchState.Loaded),

        taxDocumentsForYear: state =>
            state.documentsPerYear[state.year] && state.documentsPerYear[state.year].fetchState === FetchState.Loaded
                ? state.documentsPerYear[state.year].list.documents
                : [],

        isDocumentsForYear: state => (year: number) =>
            state.documentsPerYear[year] &&
            state.documentsPerYear[year].list &&
            state.documentsPerYear[year].list.documents &&
            state.documentsPerYear[year].list.documents.length > 0,

        isPageDocumentsLoaded () {
            return this.isLoadedForYear(this.year - 2) &&
                this.isLoadedForYear(this.year - 1) &&
                this.isLoadedForYear(this.year) &&
                this.isLoadedForYear(this.year + 1);
        },

        canViewNextYearDocuments () {
            return this.isPageDocumentsLoaded && this.isDocumentsForYear(this.year + 1);
        },
        canViewPreviousYearDocuments () {
            return this.isPageDocumentsLoaded && this.isDocumentsForYear(this.year - 1) ||
                this.isPageDocumentsLoaded && this.isDocumentsForYear(this.year - 2);
        },

        taxDocumentPdfs: state => state.pdfs,
        taxDocumentLocations: state => state.anonDocuments,

        // this will not work for reactivity, but useful in Actions
        pdfFetchStateUnreactive: state => (identifier: string) => state.pdfs[identifier]
            ? state.pdfs[identifier].fetchState
            : FetchState.Uninitialized,

        // this will not work for reactivity, but useful in Actions
        anonDocumentFetchStateUnreactive: state => (identifier: string) => state.anonDocuments[identifier]
            ? state.anonDocuments[identifier].fetchState
            : FetchState.Uninitialized,
    },
    actions: {
        setYear (year: number) {
            this.year = year;
        },

        setFetchState ([ year, fetchState ]: [ number, FetchState ]) {
            const updatedState: HsaBankTaxDocumentsForYear = this.documentsPerYear[year] || { fetchState, list: null };
            updatedState.fetchState = fetchState;
            Vue.set(this.documentsPerYear, year, updatedState);
        },

        setTaxDocuments ([ year, list ]: [ number, HsaBankTaxDocumentList ]) {
            if (!list.documents) {
                list.documents = [];
            }

            const stateForYear = this.documentsPerYear[year];
            stateForYear.fetchState = FetchState.Loaded;
            stateForYear.list = list;
            Vue.set(this.documentsPerYear, year, stateForYear);
            // intialize pdfs state object
            list.documents.forEach(doc => {
                const pdfState = this.pdfs[doc.identifier];
                if (pdfState) {
                    return;
                } // don't need to initialize it

                Vue.set(this.pdfs, doc.identifier, emptyGeneratedPdfState());
                Vue.set(this.anonDocuments, doc.identifier, emptyAnonDocumentState());
            });
        },

        setTaxDocumentPdfFetchState ([ identifier, fetchState ]: [ string, FetchState ]) {
            storeHelper.vueSet(this.pdfs[identifier], "fetchState", fetchState);
        },

        setTaxDocumentPdf ({ identifier, pdfData }: { identifier: string, pdfData: Blob }) {
            storeHelper.vueSet(this.pdfs[identifier], "fetchState", FetchState.Loaded);
            storeHelper.vueSet(this.pdfs[identifier], "pdfData", pdfData);
        },

        setAnonDocumentFetchState ([ identifier, fetchState ]: [ string, FetchState ]) {
            storeHelper.vueSet(this.anonDocuments[identifier], "fetchState", fetchState);
        },

        setAnonDocumentKey ({ identifier, documentKey }: { identifier: string, documentKey: AnonDocumentKey }) {
            storeHelper.vueSet(this.anonDocuments[identifier], "fetchState", FetchState.Loaded);
            storeHelper.vueSet(this.anonDocuments[identifier], "documentKey", documentKey);
        },
        loadTaxDocumentsForYear (year: number): Promise<void> {
            if (year > currentYear ||
                (this.documentsPerYear[year] && this.documentsPerYear[year].fetchState !==
                    FetchState.Uninitialized)) {
                return Promise.resolve();
            }

            this.setFetchState([ year, FetchState.Loading ]);
            const notifierStore = useNotifierStore();
            return BankService.getHsaBankTaxDocuments(year)
                .then(response => {
                    this.setTaxDocuments([ year, response.data ]);
                })
                .catch(error => {
                    const emptyResponse: HsaBankTaxDocumentList = { documents: [] };
                    this.setTaxDocuments([ year, emptyResponse ]);

                    if (!errorIsStatus(error, 404)) {
                        const errorMessageForUser = `There was an error retrieving tax documents.`;
                        notifierStore.setDanger(errorMessageForUser);
                    }

                    return Promise.resolve();
                });
        },

        loadTaxDocumentsForPage (year: number): Promise<any> {
            this.setYear(year);

            /*
            We need to load the data for the current year, and the the data for the next year to see if we should be able to
            navigate to the page for the next year.  Similarly we need to check the previous year's data to see if we should be
            able to navigate to the page for the previous year.  However, if it is early enough in the year that the tax forms
            for the previous year have not yet been generated, then we need to be checking 2 years ago.  E.g. On Jan 1, 2023,
            the user's 1099SA has not been generated for the 2022 tax year, and so they would not be able to navigate back.
            But if they had an account in 2021, then they should be able to navigate to 2022 so they can then navigate to 2021.
            And to allow that, we need to load the data from 2 years ago as well as just the previous year.
             */
            return Promise.allSettled([
                this.loadTaxDocumentsForYear(year - 2),
                this.loadTaxDocumentsForYear(year - 1),
                this.loadTaxDocumentsForYear(year),
                this.loadTaxDocumentsForYear(year + 1)
            ]);
        },
        loadTaxDocumentsForAllYears (): Promise<any> {
            this.setYear(0); // can actually be any positive integer < 2000
            return this.loadTaxDocumentsForYear(0);
        },
        openTaxDocumentLocation (
            { identifier, linkTitle }: { identifier: string, linkTitle: string }
        ): Promise<any> {
            if (this.anonDocumentFetchStateUnreactive(identifier) === FetchState.Loaded) {
                return this.goToAnonTaxDocument(identifier);
            }

            if (
                this.anonDocumentFetchStateUnreactive(identifier) !== FetchState.Uninitialized
            ) {
                return Promise.resolve();
            }

            this.setAnonDocumentFetchState([ identifier, FetchState.Loading ]);
            const notifierStore = useNotifierStore();
            return BankService.getHsaBankTaxDocumentKey(identifier)
                .then(response => {
                    this.setAnonDocumentKey({
                        identifier,
                        documentKey: response.data
                    });

                    return true;
                })
                .catch(err => {
                    this.setAnonDocumentFetchState([ identifier, FetchState.Uninitialized ]);
                    const errorMessageForUser = `There was an error retrieving Tax Document ${linkTitle}`;
                    notifierStore.setDanger(errorMessageForUser);
                    return Promise.resolve(false);
                })
                .then(success => {
                    if (success) {
                        return this.goToAnonTaxDocument(identifier);
                    }
                });
        },

        goToAnonTaxDocument (identifier: string): Promise<any> {
            const url = bankResource.hsaBankAnonDocument(this.anonDocuments[identifier].documentKey);
            this.setAnonDocumentFetchState([ identifier, FetchState.Uninitialized ]);
            openUrl(url);
            const userFeedbackStore = useUserFeedbackStore();
            userFeedbackStore.setUserFeedbackAction(UserFeedbackEventAction.TaxForm);

            return Promise.resolve();
        }
    }
});